import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { HOLDERS_KEY, HolderSelect, useHolders } from "../components/holders/HolderSelect";
import { PageHeader } from "../components/shell/PageHeader";
import { Button } from "../components/ui/Button";
import { EmptyState, ErrorNotice, SkeletonRows } from "../components/ui/Feedback";
import { Field, SelectField } from "../components/ui/Input";
import { Pill } from "../components/ui/Status";
import { Cell, TableFrame, TableHead, TableRow } from "../components/ui/Table";
import type { Column } from "../components/ui/Table";
import { Metric, MicroLabel } from "../components/ui/Text";
import { post } from "../lib/api";
import { count, kwh } from "../lib/format";
import { holderOptions } from "../lib/holders";
import { useSession } from "../lib/session";
import type { Holder, HolderListItem, HolderType } from "../lib/types";

/**
 * Holders — who every card belongs to (docs/identity-holders-plan.md).
 *
 * A holder is a person or a group, and groups nest. An allowance is assigned to a
 * holder and pooled over everything under it, down to any member that has one of
 * its own. So this page is where quotas are set, and the tree is shown whole:
 * which pool a card draws on is a question about its ancestors, and an indented
 * list answers it at a glance where a paginated one could not.
 */

const COLUMNS: readonly Column[] = [
  { key: "name", label: "Holder", width: "34%" },
  { key: "type", label: "Type", width: "10%" },
  { key: "cards", label: "Cards", numeric: true, width: "10%" },
  { key: "policy", label: "Allowance" },
  { key: "quota", label: "Quota", numeric: true, width: "12%" },
];

const INDENT_REM = 1.25;

export function Holders() {
  const navigate = useNavigate();
  const { can } = useSession();
  const holders = useHolders();
  const [creating, setCreating] = useState(false);

  const rows = holders.data ?? [];
  const byId = new Map(rows.map((row) => [row.id, row]));
  const ordered = holderOptions(rows).flatMap((option) => {
    const row = byId.get(option.id);
    return row === undefined ? [] : [{ row, depth: option.depth }];
  });

  return (
    <>
      <PageHeader
        eyebrow="Allowances"
        title="Holders"
        metrics={
          <>
            <Metric label="Groups" value={count(rows.filter((r) => r.type === "group").length)} />
            <Metric label="People" value={count(rows.filter((r) => r.type === "person").length)} />
            <Metric
              label="With a policy"
              value={count(rows.filter((r) => r.allowance?.holder.id === r.id).length)}
              hint="their own, not inherited"
            />
          </>
        }
        lede="A policy set on a group is shared by everything under it, until a member has one of its own."
        {...(can("identities.create") && !creating
          ? {
              actions: (
                <Button variant="primary" onClick={() => setCreating(true)}>
                  New holder
                </Button>
              ),
            }
          : {})}
      />

      {creating && can("identities.create") ? (
        <CreateHolder
          onClose={() => setCreating(false)}
          onCreated={(holder) => navigate(`/holders/${holder.id}`)}
        />
      ) : null}

      {holders.isError ? (
        <ErrorNotice error={holders.error} onRetry={() => void holders.refetch()} />
      ) : null}

      <TableFrame>
        <TableHead columns={COLUMNS} />
        {holders.isPending ? (
          <SkeletonRows rows={6} columns={COLUMNS.length} />
        ) : (
          <tbody>
            {ordered.map(({ row, depth }, index) => (
              <HolderRow
                key={row.id}
                row={row}
                depth={depth}
                index={index}
                onOpen={() => navigate(`/holders/${row.id}`)}
              />
            ))}
          </tbody>
        )}
      </TableFrame>

      {!holders.isPending && rows.length === 0 ? (
        <EmptyState
          title="No holders yet."
          detail="Express, the default group, is created by the migration; if it is missing, the database has not been migrated."
        />
      ) : null}
    </>
  );
}

function HolderRow({
  row,
  depth,
  index,
  onOpen,
}: {
  readonly row: HolderListItem;
  readonly depth: number;
  readonly index: number;
  readonly onOpen: () => void;
}) {
  const own = row.allowance !== null && row.allowance.holder.id === row.id;

  return (
    <TableRow index={index} onActivate={onOpen}>
      <Cell>
        <span
          className="flex items-baseline gap-2"
          style={{ paddingInlineStart: `${depth * INDENT_REM}rem` }}
        >
          {depth > 0 ? (
            <span aria-hidden className="text-ink-faint">
              └
            </span>
          ) : null}
          <span className="font-medium text-ink">{row.name}</span>
          {row.isSystem ? <Pill tone="idle">system</Pill> : null}
        </span>
      </Cell>
      <Cell muted>{row.type}</Cell>
      <Cell numeric muted={row.identityCount === 0}>
        {count(row.identityCount)}
      </Cell>
      <Cell muted={row.allowance === null}>
        {row.allowance === null ? (
          "None"
        ) : (
          <span className="flex flex-col gap-0.5">
            <span className={own ? "font-medium text-ink" : ""}>{row.allowance.policy.name}</span>
            {own ? null : (
              <span className="text-micro tracking-normal normal-case text-ink-faint">
                inherited from {row.allowance.holder.name}
              </span>
            )}
          </span>
        )}
      </Cell>
      <Cell numeric muted={!own}>
        {row.allowance === null ? "—" : `${kwh(row.allowance.policy.quotaWh)} kWh`}
      </Cell>
    </TableRow>
  );
}

function CreateHolder({
  onClose,
  onCreated,
}: {
  readonly onClose: () => void;
  readonly onCreated: (holder: Holder) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [type, setType] = useState<HolderType>("group");
  const [parentHolderId, setParentHolderId] = useState("");
  const holders = useHolders();
  const groups = holderOptions(holders.data ?? []).filter((option) => option.type === "group");

  const create = useMutation({
    mutationFn: (body: unknown) => post<Holder>("/holders", body),
    onSuccess: (holder) => {
      void queryClient.invalidateQueries({ queryKey: HOLDERS_KEY });
      onCreated(holder);
    },
  });

  const canSubmit = name.trim() !== "" && !create.isPending;

  return (
    <section
      aria-label="New holder"
      className="flex flex-col gap-4 border border-rule bg-paper px-4 py-4"
    >
      <div className="flex items-baseline justify-between gap-4">
        <MicroLabel>New holder</MicroLabel>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>

      {create.isError ? <ErrorNotice error={create.error} /> : null}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!canSubmit) return;
          create.mutate({
            name: name.trim(),
            type,
            ...(parentHolderId === "" ? {} : { parentHolderId }),
          });
        }}
        className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2 xl:grid-cols-4"
      >
        <Field
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={120}
          required
          autoFocus
          placeholder="Night shift, or a driver's name"
        />
        <SelectField
          label="Type"
          value={type}
          onChange={(event) => setType(event.target.value as HolderType)}
          hint={
            type === "group"
              ? "Holds cards and other holders."
              : "Holds cards only — a person cannot contain other holders."
          }
        >
          <option value="group">Group</option>
          <option value="person">Person</option>
        </SelectField>
        <HolderSelect
          label="Inside"
          value={parentHolderId}
          onChange={setParentHolderId}
          options={groups}
          emptyLabel="Top level"
          hint="Only a group can hold other holders."
        />
        <div className="flex items-end">
          <Button type="submit" variant="primary" disabled={!canSubmit} pending={create.isPending}>
            Create
          </Button>
        </div>
      </form>
    </section>
  );
}
